import React, { useState, useEffect, useRef } from 'react';
import gsap from 'gsap';
import { Eye, EyeOff } from 'lucide-react';
import { BASE_URL, ENDPOINTS } from '../../apiConfig';
import logo from '../../assets/logo.png';
import './AuthModal.css';

const AuthModal = ({ isOpen, onClose, onLoginSuccess }) => {
  const [isLogin, setIsLogin] = useState(false);
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const modalRef = useRef(null);
  const overlayRef = useRef(null);

  // Form States
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    password: '',
    phone: '',
    address: '',
    role: 'customer'
  });

  useEffect(() => {
    if (isOpen) {
      setIsLogin(false);
      setStep(1);
      gsap.set(overlayRef.current, { display: 'flex', opacity: 0 });
      gsap.to(overlayRef.current, { opacity: 1, duration: 0.4 });

      gsap.fromTo(modalRef.current,
        { y: 50, opacity: 0, scale: 0.9 },
        { y: 0, opacity: 1, scale: 1, duration: 0.6, ease: "power4.out" }
      );

      // Stagger animation for initial inputs
      gsap.fromTo('.input-group',
        { y: 20, opacity: 0 },
        { y: 0, opacity: 1, duration: 0.5, stagger: 0.1, ease: "power2.out", delay: 0.2 }
      );
    } else {
      gsap.to(overlayRef.current, {
        opacity: 0,
        duration: 0.3,
        onComplete: () => gsap.set(overlayRef.current, { display: 'none' })
      });
    }
  }, [isOpen]);

  useEffect(() => {
    // Animate step transition
    gsap.fromTo('.step-content .input-group',
      { y: 20, opacity: 0 },
      { y: 0, opacity: 1, duration: 0.5, stagger: 0.1, ease: "power2.out" }
    );
  }, [step, isLogin]);

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    // Check localStorage first for updated credentials (Simulated Backend)
    const persistentPwd = localStorage.getItem(`pwd_${formData.email}`);
    const storedUser = JSON.parse(localStorage.getItem('user') || '{}');

    if (persistentPwd === formData.password || (storedUser.email === formData.email && storedUser.password === formData.password)) {
      setTimeout(() => {
        setLoading(false);
        // If we have a stored user use it, otherwise mock a basic user object
        const loginUser = storedUser.email === formData.email ? storedUser : {
          email: formData.email,
          fullName: formData.email.split('@')[0]
        };

        // Retrieve persistent image if it exists
        const persistentImg = localStorage.getItem(`img_${formData.email}`);
        if (persistentImg) {
          loginUser.profileImage = persistentImg;
        }

        localStorage.setItem('user', JSON.stringify(loginUser));
        localStorage.setItem('token', 'simulated-token'); // Ensure token is present

        setSuccessMessage('WELCOME BACK');
        setShowSuccess(true);
        setTimeout(() => {
          onLoginSuccess(loginUser);
          onClose();
          setTimeout(() => setShowSuccess(false), 500);
        }, 1500);
      }, 800);
      return;
    }

    try {
      const response = await fetch(`${BASE_URL}${ENDPOINTS.LOGIN}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: formData.email,
          password: formData.password,
          role: 'customer'
        })
      });
      const data = await response.json();
      if (response.ok) {
        localStorage.setItem('token', data.data.token);
        localStorage.setItem('user', JSON.stringify(data.data.user));

        setSuccessMessage('WELCOME BACK');
        setShowSuccess(true);
        setTimeout(() => {
          onLoginSuccess(data.data.user);
          onClose();
          setTimeout(() => setShowSuccess(false), 500);
        }, 1500);
      } else {
        alert(data.message || 'Login Failed');
      }
    } catch (error) {
      console.error("Login error:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await fetch(`${BASE_URL}${ENDPOINTS.REGISTER}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      const data = await response.json();
      if (response.ok) {
        setSuccessMessage('ACCOUNT CREATED');
        setShowSuccess(true);
        setTimeout(() => {
          setShowSuccess(false);
          setIsLogin(true);
          setStep(1);
        }, 1500);
      } else {
        alert(data.message || 'Registration Failed');
      }
    } catch (error) {
      console.error("Register error:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-overlay" ref={overlayRef} onClick={onClose}>
      <div className="auth-modal" ref={modalRef} onClick={(e) => e.stopPropagation()}>
        {/* Luxury Flares */}
        <div className="auth-flare auth-flare-gold"></div>
        <div className="auth-flare auth-flare-purple"></div>

        {/* Elite Monogram Icon */}
        <div className="auth-monogram">
          <div className="monogram-diamond"></div>
          <span className="monogram-letter">E</span>
        </div>

        <button className="close-btn" onClick={onClose} aria-label="Close modal">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
            <path d="M18 6L6 18M6 6l12 12" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>

        {showSuccess ? (
          <div className="auth-success-popup">
            <div className="success-icon-wrapper">
              <img src={logo} alt="Escentrum Perfume" className="success-perfume-logo" />
            </div>
            <h3 className="success-title">{successMessage}</h3>
            <p className="success-subtitle">ESCENTRUM FRAGRANCE</p>
          </div>
        ) : (
          <>
            <div className="auth-header">
              <h2>{isLogin ? 'WELCOME BACK' : 'CREATE ACCOUNT'}</h2>
              <p>{isLogin ? 'Enter your elite credentials' : 'Join the world of Escentrum'}</p>
            </div>

            {isLogin ? (
              <form className="auth-form" onSubmit={handleLogin}>
                <div className="input-group mt-spaced">
                  <label>EMAIL ADDRESS</label>
                  <input type="email" name="email" placeholder="email@example.com" onChange={handleInputChange} required />
                </div>
                <div className="input-group">
                  <label>PASSWORD</label>
                  <div className="password-input-wrapper">
                    <input
                      type={showPassword ? "text" : "password"}
                      name="password"
                      placeholder="••••••••"
                      onChange={handleInputChange}
                      required
                    />
                    <button
                      type="button"
                      className="password-toggle-btn"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>
                <button type="submit" className="auth-submit-btn" disabled={loading}>
                  {loading ? 'AUTHENTICATING...' : 'SIGN IN'}
                </button>
                <p className="switch-auth">
                  Don't have an account? <span onClick={() => setIsLogin(false)}>JOIN NOW</span>
                </p>
              </form>
            ) : (
              <div className="auth-stepper-container">
                <div className="stepper-progress">
                  <div className={`step-dot ${step >= 1 ? 'active' : ''}`}>1</div>
                  <div className="step-line"></div>
                  <div className={`step-dot ${step >= 2 ? 'active' : ''}`}>2</div>
                </div>

                <form className="auth-form" onSubmit={step === 1 ? (e) => { e.preventDefault(); setStep(2); } : handleRegister}>
                  {step === 1 ? (
                    <div className="step-content">
                      <div className="input-group">
                        <label>FULL NAME</label>
                        <input type="text" name="fullName" placeholder="John Doe" onChange={handleInputChange} required />
                      </div>
                      <div className="input-group mt-spaced">
                        <label>EMAIL ADDRESS</label>
                        <input type="email" name="email" placeholder="email@example.com" onChange={handleInputChange} required />
                      </div>
                      <div className="input-group mt-spaced">
                        <label>PHONE NUMBER</label>
                        <input type="text" name="phone" placeholder="+1 234 567 890" onChange={handleInputChange} required />
                      </div>
                      <button type="submit" className="auth-submit-btn">CONTINUE</button>
                    </div>
                  ) : (
                    <div className="step-content">
                      <div className="input-group">
                        <label>PASSWORD</label>
                        <div className="password-input-wrapper">
                          <input
                            type={showPassword ? "text" : "password"}
                            name="password"
                            placeholder="••••••••"
                            onChange={handleInputChange}
                            required
                          />
                          <button
                            type="button"
                            className="password-toggle-btn"
                            onClick={() => setShowPassword(!showPassword)}
                          >
                            {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                          </button>
                        </div>
                      </div>
                      <div className="input-group mt-spaced">
                        <label>SHIPPING ADDRESS</label>
                        <input type="text" name="address" placeholder="123 Luxury St, NY" onChange={handleInputChange} required />
                      </div>
                      <div className="btn-row">
                        <button type="button" className="auth-back-btn" onClick={() => setStep(1)}>BACK</button>
                        <button type="submit" className="auth-submit-btn" disabled={loading}>
                          {loading ? 'REGISTERING...' : 'COMPLETE'}
                        </button>
                      </div>
                    </div>
                  )}
                </form>
                <p className="switch-auth">
                  Already a member? <span onClick={() => setIsLogin(true)}>SIGN IN</span>
                </p>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default AuthModal;
